import { Invitation } from 'sip.js';
/** Max wait for REFER subscription NOTIFY (sipfrag) before surfacing a timeout hint. */
const REFER_NOTIFY_TIMEOUT_MS = 60_000;
function parseSipfragStatus(body) {
    const line = body
        .trim()
        .split(/\r?\n/)
        .find((l) => /^SIP\/2\.0\s+\d{3}/i.test(l.trim())) ?? body.trim();
    const match = line.match(/^SIP\/2\.0\s+(\d{3})(?:\s+(.*))?/i);
    if (!match)
        return undefined;
    return { statusCode: Number(match[1]), phrase: match[2]?.trim() };
}
function isReferSubscriptionTerminated(subscriptionState) {
    return Boolean(subscriptionState && /^terminated\b/i.test(subscriptionState.trim()));
}
/**
 * SIP REFER options that accept in-dialog NOTIFY (RFC 3515 sipfrag) and resolve when the
 * transfer attempt reaches a terminal outcome. Keeps the session established until then so
 * Asterisk/PBX refer subscriptions are not answered with 481.
 */
export function createReferAwaitOptions(timeoutMs = REFER_NOTIFY_TIMEOUT_MS) {
    let resolveWait;
    const waitPromise = new Promise((resolve) => {
        resolveWait = resolve;
    });
    let settled = false;
    const settle = (result) => {
        if (settled)
            return;
        settled = true;
        window.clearTimeout(timer);
        resolveWait(result);
    };
    const timer = window.setTimeout(() => settle({ outcome: 'timeout' }), timeoutMs);
    const onNotify = (notification) => {
        const status = parseSipfragStatus(notification.request.body ?? '');
        const subscriptionState = notification.request.getHeader('Subscription-State');
        void notification.accept().catch(() => undefined);
        if (status) {
            if (status.statusCode >= 200 && status.statusCode < 300) {
                settle({ outcome: 'success', statusCode: status.statusCode });
                return;
            }
            if (status.statusCode >= 300) {
                const detail = status.phrase ? `${status.statusCode} ${status.phrase}` : String(status.statusCode);
                settle({ outcome: 'failure', statusCode: status.statusCode, message: `Transfer failed: ${detail}` });
                return;
            }
        }
        if (isReferSubscriptionTerminated(subscriptionState)) {
            if (status && status.statusCode >= 200 && status.statusCode < 300) {
                settle({ outcome: 'success', statusCode: status.statusCode });
            }
            else {
                settle({
                    outcome: 'failure',
                    statusCode: status?.statusCode,
                    message: 'Transfer subscription ended before success.',
                });
            }
        }
    };
    return {
        onNotify,
        waitForReferNotify: () => waitPromise,
        cancelReferWait: (message) => settle({ outcome: 'failure', message }),
    };
}
/** If REGISTER neither succeeds nor rejects (e.g. WS up but registrar silent), surfacing the usual cause avoids an endless spinner. */
export const REGISTER_PENDING_HINT_MS = 15_000;
/** PBX SDP must advertise DTLS-SRTP (`a=fingerprint`). Shown when setRemoteDescription fails for that reason. */
export const SDP_MISSING_DTLS_FINGERPRINT_HINT = 'WebRTC media error: SDP had no DTLS fingerprint (browser requires DTLS-SRTP). Configure this extension’s PJSIP endpoint for WebRTC on Asterisk (e.g. webrtc=yes, or media_encryption=dtls with working dtls certs; avoid SDES-only offers). Plain SIP desk phones are more permissive than the browser.';
/**
 * Inbound answer: cap how long the WebRTC layer waits for ICE gathering before sending 200 OK.
 * Without this, browsers often wait several seconds before the call appears “connected” / audio flows.
 */
export const ANSWER_INVITE_OPTIONS = {
    sessionDescriptionHandlerOptions: {
        iceGatheringTimeout: 2000,
    },
};
export function callerLabelFromSession(session) {
    try {
        const ri = session.remoteIdentity;
        const name = ri.displayName?.trim();
        if (name)
            return name;
        const u = ri.uri.user;
        if (u)
            return String(u);
        return ri.uri.toString();
    }
    catch {
        return 'Unknown caller';
    }
}
const QUEUE_SIP_HEADER_NAMES = [
    'X-Queue-Name',
    'X-Queue',
    'Queue-Name',
    'Queue',
    'X-Asterisk-Queue',
    'X-QueueName',
];
const CALLED_SIP_HEADER_NAMES = [
    'X-Called-Number',
    'X-DID',
    'X-Original-DID',
    'Called-Number',
    'P-Called-Party-ID',
    'PCalledPartyID',
];
function readInvitationHeader(session, names) {
    if (!(session instanceof Invitation))
        return undefined;
    for (const name of names) {
        const raw = session.request.getHeader(name)?.trim();
        if (raw)
            return raw;
    }
    return undefined;
}
/** Pull a user part or digit string from a SIP name-addr or plain value. */
function digitsOrUriUser(value) {
    const angle = value.match(/<sip:([^@>;]+)/i);
    if (angle?.[1])
        return angle[1].trim();
    const bare = value.match(/sip:([^@>;]+)/i);
    if (bare?.[1])
        return bare[1].trim();
    const digits = value.replace(/\D/g, '');
    return digits || value.trim();
}
export function queueNameFromSession(session) {
    const raw = readInvitationHeader(session, QUEUE_SIP_HEADER_NAMES);
    if (!raw)
        return null;
    const cleaned = raw.replace(/^["']|["']$/g, '').trim();
    return cleaned || null;
}
export function calledNumberFromSession(session) {
    const raw = readInvitationHeader(session, CALLED_SIP_HEADER_NAMES);
    if (raw) {
        const parsed = digitsOrUriUser(raw);
        return parsed || null;
    }
    return null;
}
export function incomingRingFromSession(session) {
    return {
        callerLabel: callerLabelFromSession(session),
        queueName: queueNameFromSession(session),
        calledNumber: calledNumberFromSession(session),
    };
}
export function registerRejectMessage(response) {
    try {
        const { statusCode, reasonPhrase } = response.message ?? {};
        return `REGISTER rejected: ${statusCode ?? '?'} ${reasonPhrase ?? ''}`.trim();
    }
    catch {
        return 'REGISTER rejected by server.';
    }
}
export function sipResponseLine(prefix, response) {
    try {
        const { statusCode, reasonPhrase } = response.message ?? {};
        return `${prefix}: ${statusCode ?? '?'} ${reasonPhrase ?? ''}`.trim();
    }
    catch {
        return `${prefix} (no response details).`;
    }
}
export function looksLikeMissingDtlsFingerprintError(message) {
    const m = message.toLowerCase();
    return (m.includes('dtls fingerprint') ||
        (m.includes('sdp') && m.includes('setremotedescription')) ||
        (m.includes('invalidaccesserror') && m.includes('remotedescription')));
}
export function mapSipMediaException(e) {
    const msg = e instanceof Error ? e.message : String(e);
    const lower = msg.toLowerCase();
    if (lower.includes('insecure context') ||
        lower.includes('media devices not available')) {
        return ('Microphone/WebRTC requires a secure context (HTTPS or http://localhost). ' +
            'Open the app via https://… or http://127.0.0.1:3000 — not http://10.31.11.7 or another LAN IP over plain HTTP.');
    }
    return looksLikeMissingDtlsFingerprintError(msg) ? SDP_MISSING_DTLS_FINGERPRINT_HINT : msg;
}
/** Browsers block getUserMedia on http:// except localhost / 127.0.0.1. */
export function insecureContextSipHint() {
    if (typeof window === 'undefined' || window.isSecureContext) {
        return null;
    }
    const host = window.location.hostname;
    return (`WebRTC softphone requires HTTPS (or http://localhost). This page is ${window.location.protocol}//${host} — ` +
        'use TLS in production or http://127.0.0.1:3000 for local testing.');
}
